import { grey } from '@mui/material/colors'
import get from 'lodash/get'

import { isPlacement } from 'components/inventory/containers/utils'
import { CONTAINER_STATUS, CONTAINER_STATUS_META } from 'containers/inventory/settings'
import { UNASSIGNED, COMMON } from 'theme/colors'
import { getFormattedColorAndContrastColor } from 'theme/utils'

import type { PlacementCandidate } from 'components/inventory/containers/utils'

// Only the slice this reads. A full MUI Theme satisfies it; so does a test stub.
type MarkerTheme = { palette: { action: { active: string } } }

// Flat feature properties or a nested container row; both shapes reach this from the map layers.
export type ContainerMarkerProperties = PlacementCandidate & {
  status?: string | null
  color?: { color?: string | null } | string | null
  // Retints the border without inventing a status — set by the snap review's "old location" ghost.
  _pillBorderColor?: string | null
}

export type ContainerMarkerColors = {
  outerColor: string
  innerBgColor: string
  labelColor: string
  iconBgColor: string
  iconColor: string
  dashed: boolean
}

// Matches the DOM ContainerMarker's getProps() semantics so the WebGL pill and the DOM fallback render with identical colors. Accepts flat or nested feature properties.
export function resolveContainerColors(properties: ContainerMarkerProperties, theme: MarkerTheme): ContainerMarkerColors {
  const color = get(properties, 'color.color') || get(properties, 'color')
  const formattedColor = typeof color === 'string' ? color : null
  const { contrastColor } = getFormattedColorAndContrastColor(formattedColor, theme)
  const { status } = properties
  const meta = status ? get(CONTAINER_STATUS_META, status) : undefined
  // Neutral because it has no status to borrow, dashed because it is not physically there.
  const placement = isPlacement(properties)
  const statusBorderColor = placement ? grey[500] : get(meta, 'color', UNASSIGNED.main)

  // `_pillBorderColor` retints without inventing a status — the snap review's ghost is the same container in grey.
  const borderColor = get(properties, '_pillBorderColor') || statusBorderColor
  const isDecommissioned = status === CONTAINER_STATUS.DECOMMISSIONED

  if (isDecommissioned) {
    return {
      outerColor: borderColor,
      innerBgColor: grey[700],
      labelColor: COMMON.white,
      dashed: placement,
      // Match the DOM: the icon Box has no bgcolor when iconBgColor is empty, so the chip's grey[700] shows through.
      iconBgColor: grey[700],
      iconColor: COMMON.white,
    }
  }

  return {
    outerColor: borderColor,
    innerBgColor: COMMON.white,
    labelColor: COMMON.black,
    dashed: placement,
    // Intentional WebGL-only divergence from the DOM: when a container has no color, the DOM lets the chip's white show through (icon region indistinguishable from the label region); fall back to grey[100] here so the icon region stays visually distinct.
    iconBgColor: formattedColor || grey[100],
    iconColor: formattedColor ? contrastColor : theme.palette.action.active,
  }
}
